/* eslint-disable @typescript-eslint/no-duplicate-enum-values */

import type { SubmissionErrorState } from '@/utils/formSubmission';

export enum LoanReasons {
  debt_consolidation = 'Debt Consolidation',
  credit_card_refinance = 'Credit card refinance',
  home_improvement = 'Home Improvement',
}

export enum PaymentFrequency {
  weekly = 'Weekly',
  biWeekly = 'Bi-Weekly',
  semiMonthly = 'Semi-Monthly',
  monthly = 'Monthly',
}

export type DebtValue =
  | '$0 - $9,999'
  | '$10,000 - $14,999'
  | '$15,000 - $19,999'
  | '$20,000 - $29,999'
  | '$30,000 - $39,999'
  | '$40,000 - $49,999'
  | '$50,000 - $59,999'
  | '$60,000 - $69,999'
  | '$70,000 - $79,999'
  | '$80,000 - $89,999'
  | '$90,000 - $99,999'
  | '$100,000+';

export enum CreditRating {
  excellent = 'Excellent (720+)',
  good = 'Good (660-719)',
  fair = 'Fair (620-659)',
  poor = 'Poor (<600)',
}

export enum EducationalLevel {
  high_school_diploma = 'High school diploma',
  associate_degree = "Associate's degree",
  bachelor_degree = "Bachelor's degree",
  master_degree = "Master's degree",
  other_degree = 'Other graduate degree',
  other = 'Other',
}

export enum EmploymentStatus {
  employed = 'Employed',
  employed_full_time = 'Employed full time',
  employed_part_time = 'Employed part time',
  military = 'Military',
  not_employed = 'Not employed',
  self_employed = 'Self-employed',
  retired = 'Retired',
  other = 'Other',
}

export enum USStates {
  Alabama = 'AL',
  Alaska = 'AK',
  Arizona = 'AZ',
  Arkansas = 'AR',
  California = 'CA',
  Colorado = 'CO',
  Connecticut = 'CT',
  Delaware = 'DE',
  Washington_DC = 'DC',
  Florida = 'FL',
  Georgia = 'GA',
  Hawaii = 'HI',
  Idaho = 'ID',
  Illinois = 'IL',
  Indiana = 'IN',
  Iowa = 'IA',
  Kansas = 'KS',
  Kentucky = 'KY',
  Louisiana = 'LA',
  Maine = 'ME',
  Maryland = 'MD',
  Massachusetts = 'MA',
  Michigan = 'MI',
  Minnesota = 'MN',
  Mississippi = 'MS',
  Missouri = 'MO',
  Montana = 'MT',
  Nebraska = 'NE',
  Nevada = 'NV',
  New_Hampshire = 'NH',
  New_Jersey = 'NJ',
  New_Mexico = 'NM',
  New_York = 'NY',
  North_Carolina = 'NC',
  North_Dakota = 'ND',
  Ohio = 'OH',
  Oklahoma = 'OK',
  Oregon = 'OR',
  Pennsylvania = 'PA',
  Rhode_Island = 'RI',
  South_Carolina = 'SC',
  South_Dakota = 'SD',
  Tennessee = 'TN',
  Texas = 'TX',
  Utah = 'UT',
  Vermont = 'VT',
  Virginia = 'VA',
  Washington = 'WA',
  West_Virginia = 'WV',
  Wisconsin = 'WI',
  Wyoming = 'WY',
}

export interface FormData {
  first_name: string;
  last_name: string;
  borrow_sum: number | string;
  loan_reason: LoanReasons | null;
  annual_income: number | string;
  payment_frequency: PaymentFrequency | null;
  unsecured_debt_value: DebtValue | null;
  address: string;
  address_variant: 'manual' | 'google';
  city: string;
  apt: string;
  state: USStates | null;
  zip: string;
  house_ownership: 'rent' | 'own with outright' | 'own with mortgage' | null;
  email: string;
  phone: string;
  creditRating: CreditRating | null;
  educational_level: EducationalLevel | null;
  employment_status: EmploymentStatus | null;
  date_of_birth: string;
  ssn: string;
}

export type FormVariant =
  | 'default'
  | 'agent'
  | 'self-service'
  | 'calculator'
  | 'referral'
  | 'reapply';

export enum BESValue {
  AL = 1,
  AK = 1,
  AZ = 1,
  AR = 1,
  CA = 2,
  CO = 2,
  CT = 1,
  DE = 0,
  FL = 1,
  GA = 1,
  HI = 0,
  ID = 0,
  IL = 1,
  IN = 1,
  IA = 0,
  KS = 1,
  KY = 2,
  LA = 1,
  ME = 1,
  MD = 1,
  MA = 1,
  MI = 1,
  MN = 0,
  MS = 1,
  MO = 1,
  MT = 0,
  NE = 1,
  NV = 1,
  NH = 3,
  NJ = 1,
  NM = 1,
  NY = 1,
  NC = 1,
  ND = 0,
  OH = 1,
  OK = 1,
  OR = 0,
  PA = 1,
  RI = 0,
  SC = 1,
  SD = 1,
  TN = 1,
  TX = 1,
  UT = 1,
  VT = 0,
  VA = 1,
  WA = 0,
  DC = 1,
  WV = 2,
  WI = 0,
  WY = 0,
}

export enum LEGValue {
  AL = 2,
  AK = 2,
  AZ = 2,
  AR = 2,
  CA = 2,
  CO = 2,
  CT = 1,
  DE = 0,
  FL = 2,
  GA = 1,
  HI = 0,
  ID = 0,
  IL = 1,
  IN = 2,
  IA = 0,
  KS = 1,
  KY = 2,
  LA = 2,
  ME = 1,
  MD = 2,
  MA = 2,
  MI = 2,
  MN = 0,
  MS = 2,
  MO = 2,
  MT = 0,
  NE = 2,
  NV = 2,
  NH = 1,
  NJ = 1,
  NM = 2,
  NY = 2,
  NC = 2,
  ND = 0,
  OH = 1,
  OK = 2,
  OR = 0,
  PA = 2,
  RI = 0,
  SC = 1,
  SD = 2,
  TN = 2,
  TX = 2,
  UT = 2,
  VT = 0,
  VA = 1,
  WA = 0,
  DC = 2,
  WV = 1,
  WI = 2,
  WY = 0,
}

export const allowedEmailDomains = [
  'com',
  'net',
  'edu',
  'org',
  'me',
  'us',
  'co',
  'gov',
];

export interface FormStatus {
  userId: string | null;
  loading: boolean;
  submissionLoading: boolean;
  redirectValue: string;
  stepLoading: boolean;
  loadingCheckmark: null | 'show' | 'hide';
  verificationError: boolean;
  submissionError: SubmissionErrorState | null;
}
